import { Component, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AdminService } from '../../../services/admin/admin.service';
import { ApiServiceService } from '../../../services/common/api-service.service';

@Component({
  selector: 'app-user-manager',
  templateUrl: './user-manager.component.html',
  styleUrls: ['./user-manager.component.scss']
})
export class UserManagerComponent implements OnInit {
  users: any[] = [];
  loading = false;
  currentPage = 1;
  totalPages = 1;

  searchQuery = '';
  filterGender = '';
  filterPlan = '';
  filterSort = 'newest';

  planModalUser: any = null;
  detailModalUser: any = null;
  loadingDetail = false;
  newPlan = 'GOLD';
  expiryDays = 30;
  planChangeReason = '';
  saving = false;
  base64Images: { [key: string]: string } = {};

  private searchSubject = new Subject<string>();

  constructor(private adminService: AdminService, private apiService: ApiServiceService) {}

  ngOnInit(): void {
    this.loadUsers();
    this.searchSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
      this.currentPage = 1;
      this.loadUsers();
    });
  }

  loadUsers(): void {
    this.loading = true;
    const params: any = {
      page: this.currentPage,
      limit: 15,
      sort: this.filterSort,
    };
    if (this.filterGender) params['gender'] = this.filterGender;
    if (this.filterPlan) params['plan'] = this.filterPlan;
    if (this.searchQuery) params['search'] = this.searchQuery;

    this.adminService.getUsers(params).subscribe({
      next: res => {
        this.loading = false;
        if (res.status === 'success') {
          this.users = res.data.users;
          this.totalPages = res.data.pages;
          this.loadProfileImages(this.users);
        }
      },
      error: err => { this.loading = false; console.error('[UserManager]', err); }
    });
  }

  onSearch(): void { this.searchSubject.next(this.searchQuery); }

  changePage(page: number): void {
    this.currentPage = page;
    this.loadUsers();
  }

  getFullName(user: any): string {
    if (!user) return '(No Name)';
    const bi = user.basic_info || {};
    return [bi.first_name, bi.last_name].filter(Boolean).join(' ') || '(No Name)';
  }

  getInitials(user: any): string {
    const name = this.getFullName(user);
    return name.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase();
  }

  loadProfileImages(usersList: any[]): void {
    if (!usersList || !Array.isArray(usersList)) return;
    usersList.forEach(user => {
      if (!user || !user._id) return;
      if (this.base64Images[user._id]) return;

      if (user.personal_info?.avatar_Id) {
        const idKey = user.basic_info?.keycloak_id || user.basic_info?.ory_identity_id || user._id;
        const imgPath = `${idKey}/gallery/avatar/${user.personal_info.avatar_Id}`;
        this.apiService.getImageBase64(imgPath).subscribe({
          next: (resp: any) => {
            if (resp && resp.base64Image) {
              const b64 = resp.base64Image;
              this.base64Images[user._id] = b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
            }
          },
          error: () => {}
        });
      } else if (user.personal_info?.profile_picture && typeof user.personal_info.profile_picture === 'string') {
        const pic = user.personal_info.profile_picture;
        if (pic.startsWith('http') || pic.startsWith('data:')) {
          this.base64Images[user._id] = pic;
        } else if (pic.length > 200) {
          this.base64Images[user._id] = `data:image/jpeg;base64,${pic}`;
        } else {
          this.apiService.getImageBase64(pic).subscribe({
            next: (resp: any) => {
              if (resp && resp.base64Image) {
                const b64 = resp.base64Image;
                this.base64Images[user._id] = b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
              }
            },
            error: () => {}
          });
        }
      }
    });
  }

  getProfilePhoto(user: any): string | null {
    if (!user || !user._id) return null;
    if (this.base64Images[user._id]) return this.base64Images[user._id];
    if (user.personal_info?.profile_picture && typeof user.personal_info.profile_picture === 'string') {
      const pic = user.personal_info.profile_picture;
      if (pic.startsWith('http') || pic.startsWith('data:')) return pic;
      if (pic.length > 200) return `data:image/jpeg;base64,${pic}`;
    }
    return null;
  }

  getVal(val: any): string {
    if (val === null || val === undefined || val === '') return '—';
    return String(val);
  }

  getLookup(obj: any, ...fields: string[]): string {
    if (!obj) return '—';
    if (typeof obj === 'string') return obj;
    for (const f of fields) {
      if (obj[f] !== undefined && obj[f] !== null && obj[f] !== '') {
        return String(obj[f]);
      }
    }
    return '—';
  }

  getLookupList(arr: any, ...fields: string[]): string {
    if (!arr) return '—';
    if (!Array.isArray(arr)) return this.getLookup(arr, ...fields);
    const items = arr.map((item: any) => this.getLookup(item, ...fields)).filter(v => v && v !== '—');
    return items.length ? items.join(', ') : '—';
  }

  getLanguagesSpoken(user: any): string {
    if (!user?.personal_info?.language_id || !Array.isArray(user.personal_info.language_id)) return '—';
    const langs = user.personal_info.language_id.map((l: any) => 
      typeof l === 'string' ? l : (l?.language_name || l?.language_nme || '')
    ).filter(Boolean);
    return langs.length ? langs.join(', ') : '—';
  }

  getAddress(user: any): string {
    const loc = user?.residing_location;
    if (!loc) return '—';
    const parts = [loc.address1, loc.address2].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  }

  openDetail(user: any): void {
    this.loadingDetail = true;
    this.detailModalUser = user;
    this.loadProfileImages([user]);
    this.adminService.getUserDetail(user._id).subscribe({
      next: res => {
        this.loadingDetail = false;
        if (res.status === 'success' && res.data) {
          this.detailModalUser = res.data;
          this.loadProfileImages([this.detailModalUser]);
        }
      },
      error: err => {
        this.loadingDetail = false;
        console.error('[UserManager]', err);
      }
    });
  }

  closeDetailModal(): void {
    this.detailModalUser = null;
  }

  openPlanModal(user: any): void {
    this.planModalUser = user;
    this.newPlan = user.current_plan || 'GOLD';
    this.expiryDays = 30;
    this.planChangeReason = '';
  }

  closePlanModal(): void { this.planModalUser = null; }

  savePlanChange(): void {
    if (!this.planModalUser) return;
    this.saving = true;
    this.adminService.updateUserPlan(this.planModalUser._id, this.newPlan, this.expiryDays, this.planChangeReason).subscribe({
      next: res => {
        this.saving = false;
        if (res.status === 'success') {
          const idx = this.users.findIndex(u => u._id === this.planModalUser._id);
          if (idx !== -1) this.users[idx].current_plan = this.newPlan;
          this.closePlanModal();
        }
      },
      error: err => { this.saving = false; alert('Failed: ' + (err?.error?.message || 'Error')); }
    });
  }

  toggleSuspend(user: any): void {
    const action = user.subscription_status === 'CANCELLED' ? 'activate' : 'suspend';
    if (!confirm(`Are you sure you want to ${action} this user?`)) return;
    this.adminService.updateUserStatus(user._id, action).subscribe({
      next: res => {
        if (res.status === 'success') {
          user.subscription_status = action === 'suspend' ? 'CANCELLED' : 'ACTIVE';
        }
      },
      error: err => alert('Failed: ' + (err?.error?.message || 'Error'))
    });
  }
}
